// Teacher profile service for fetching teacher data from backend
import { getApiUrl } from './apiConfig';

/**
 * Fetch teacher profile data from backend
 * @param {string} userId - The user_id of the teacher
 * @returns {Promise<Object>} Teacher profile data
 */
export const fetchTeacherProfile = async (userId, userEmail = null) => {
  try {
    if (!userId) {
      console.error('❌ No user ID provided');
      return null;
    }

    console.log('🔄 Fetching teacher profile for user_id:', userId);
    if (userEmail) {
      console.log('📧 Also have email for fallback:', userEmail);
    }

    // Build URL with email fallback
    let url = getApiUrl(`teacher-profile?user_id=${userId}`);
    if (userEmail) {
      url += `&email=${encodeURIComponent(userEmail)}`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('⚠️ Teacher profile not found for user_id:', userId);
        console.log('💡 This means teacher has not completed profile setup yet');
        console.log('📝 Teacher needs to visit /teacher-profile-form to complete setup');
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const profileData = await response.json();
    console.log('✅ Teacher profile fetched successfully from backend:', profileData);

    // Return structured data
    const structuredProfile = {
      userId: profileData.user_id || '',
      fullName: profileData.fullName || '',
      email: profileData.email || '',
      username: profileData.username || profileData.signupUsername || '',
      schoolName: profileData.schoolName || '',  // University name
      role: profileData.role || '',  // Position (e.g., "Asst. Professor")
      subject: profileData.subject || '',  // Subject they teach
      comments: profileData.comments || '',
      completedAt: profileData.completedAt || '',
      createdAt: profileData.createdAt || ''
    };

    // Save to localStorage for future use
    console.log('💾 Saving teacher profile to localStorage...');
    saveTeacherProfileToLocalStorage(structuredProfile);
    console.log('✅ Teacher profile saved to localStorage');

    return structuredProfile;

  } catch (error) {
    console.error('❌ Error fetching teacher profile:', error);
    return null;
  }
};

/**
 * Save teacher profile data to localStorage
 * @param {Object} profileData - Teacher profile data
 */
export const saveTeacherProfileToLocalStorage = (profileData) => {
  try {
    // Save as structured data
    localStorage.setItem('teacherProfileData', JSON.stringify(profileData));
    
    // Also save individual items for backward compatibility
    localStorage.setItem('teacherFullName', profileData.fullName);
    localStorage.setItem('teacherEmail', profileData.email);
    localStorage.setItem('teacherUsername', profileData.username);
    localStorage.setItem('teacherSchoolName', profileData.schoolName);
    localStorage.setItem('teacherRole', profileData.role);
    localStorage.setItem('teacherSubject', profileData.subject);
    
    console.log('💾 Teacher profile data saved to localStorage');
  } catch (error) {
    console.error('Error saving teacher profile to localStorage:', error);
  }
};

/**
 * Get teacher profile data from localStorage
 * @returns {Object} Teacher profile data
 */
export const getTeacherProfileFromLocalStorage = () => {
  try {
    // Try to get from structured data first
    const teacherData = localStorage.getItem('teacherProfileData');
    if (teacherData) {
      return JSON.parse(teacherData);
    }
    
    // Fallback to individual localStorage items
    return {
      fullName: localStorage.getItem('teacherFullName') || '',
      email: localStorage.getItem('teacherEmail') || '',
      username: localStorage.getItem('teacherUsername') || '',
      schoolName: localStorage.getItem('teacherSchoolName') || '',
      role: localStorage.getItem('teacherRole') || '',
      subject: localStorage.getItem('teacherSubject') || ''
    };
  } catch (error) {
    console.error('Error getting teacher profile from localStorage:', error);
    return {
      fullName: '',
      email: '',
      username: '',
      schoolName: '',
      role: '',
      subject: ''
    };
  }
};


